// Posturra-branded Add-to-Home-Screen onboarding sheet.
// Auto-shows once on mobile browsers (never in standalone mode, never after "Not now").
// Can be re-opened anytime via the "pstr-open-install" window event (menu item).
import { useEffect, useState } from "react";
import { Smartphone, Share, SquarePlus, X, Sparkles, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { isStandalone, isMobileDevice, isIOS, hasNativePrompt, promptInstall } from "../lib/installPrompt";

export function InstallPrompt() {
  const [open, setOpen] = useState(false);
  const [nativeReady, setNativeReady] = useState(hasNativePrompt());

  useEffect(() => {
    const openSheet = () => setOpen(true);
    const onAvailable = () => setNativeReady(true);
    const onInstalled = () => { setOpen(false); toast.success("Posturra added to your phone"); };
    window.addEventListener("pstr-open-install", openSheet);
    window.addEventListener("pstr-install-available", onAvailable);
    window.addEventListener("pstr-install-done", onInstalled);

    // Auto-show once after first login on mobile browsers
    if (isMobileDevice() && !isStandalone() && localStorage.getItem("pstr_install_dismissed") !== "1") {
      const t = setTimeout(() => setOpen(true), 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("pstr-open-install", openSheet);
        window.removeEventListener("pstr-install-available", onAvailable);
        window.removeEventListener("pstr-install-done", onInstalled);
      };
    }
    return () => {
      window.removeEventListener("pstr-open-install", openSheet);
      window.removeEventListener("pstr-install-available", onAvailable);
      window.removeEventListener("pstr-install-done", onInstalled);
    };
  }, []);

  if (!open || isStandalone()) return null;

  const notNow = () => {
    localStorage.setItem("pstr_install_dismissed", "1");
    setOpen(false);
  };

  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") {
      setOpen(false);
    } else if (outcome === "dismissed") {
      setNativeReady(hasNativePrompt());
    }
  };

  const ios = isIOS();

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center print:hidden" data-testid="install-prompt-sheet">
      <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" onClick={notNow} aria-hidden="true" />
      <div className="relative w-full sm:max-w-md bg-paper-50 rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          data-testid="install-close-btn"
          onClick={notNow}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-paper-100 inline-flex items-center justify-center text-ink-500"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-sage-500 flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="font-heading text-xl text-ink-900">Put Posturra on your phone</h2>
            <p className="text-sm text-ink-500">Faster access and an app-like experience.</p>
          </div>
        </div>

        {nativeReady ? (
          <p className="text-sm text-ink-700 mb-5">
            Add Posturra to your Home Screen — it opens full-screen, right from your phone, just like a native app.
          </p>
        ) : ios ? (
          <ol className="space-y-3 mb-5 text-sm text-ink-700" data-testid="install-ios-steps">
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-xl bg-paper-100 border border-paper-200 flex items-center justify-center shrink-0">
                <Share className="w-4 h-4 text-sage-700" strokeWidth={1.5} />
              </span>
              <span>Tap the <strong className="text-ink-900">Share</strong> button in Safari&apos;s toolbar</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-xl bg-paper-100 border border-paper-200 flex items-center justify-center shrink-0">
                <SquarePlus className="w-4 h-4 text-sage-700" strokeWidth={1.5} />
              </span>
              <span>Scroll down and tap <strong className="text-ink-900">Add to Home Screen</strong></span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-xl bg-paper-100 border border-paper-200 flex items-center justify-center shrink-0">
                <Smartphone className="w-4 h-4 text-sage-700" strokeWidth={1.5} />
              </span>
              <span>Tap <strong className="text-ink-900">Add</strong> — Posturra appears on your Home Screen</span>
            </li>
          </ol>
        ) : (
          <ol className="space-y-3 mb-5 text-sm text-ink-700" data-testid="install-android-steps">
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-xl bg-paper-100 border border-paper-200 flex items-center justify-center shrink-0">
                <MoreVertical className="w-4 h-4 text-sage-700" strokeWidth={1.5} />
              </span>
              <span>Tap your browser&apos;s <strong className="text-ink-900">menu</strong> button</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-xl bg-paper-100 border border-paper-200 flex items-center justify-center shrink-0">
                <SquarePlus className="w-4 h-4 text-sage-700" strokeWidth={1.5} />
              </span>
              <span>Choose <strong className="text-ink-900">Add to Home screen</strong> or <strong className="text-ink-900">Install app</strong></span>
            </li>
          </ol>
        )}

        <div className="flex gap-3">
          {nativeReady && (
            <button
              type="button"
              data-testid="install-app-btn"
              onClick={install}
              className="flex-1 min-h-[48px] rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium transition-all active:scale-95 inline-flex items-center justify-center gap-2"
            >
              <Smartphone className="w-4 h-4" strokeWidth={1.5} /> Add Posturra
            </button>
          )}
          <button
            type="button"
            data-testid="install-not-now-btn"
            onClick={notNow}
            className={`${nativeReady ? "" : "flex-1 "}min-h-[48px] px-6 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 font-medium transition-all`}
          >
            {nativeReady ? "Not now" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}
